import * as XLSX from "xlsx";

export const capitalizeWords = (str) => {
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
};

export const getInitials = (user) => {
  const name = [user?.firstName, user?.lastName]
    .filter(Boolean)
    .map((part) => String(part).trim())
    .filter(Boolean)
    .join(" ");

  if (name) {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .substring(0, 2)
      .toUpperCase();
  }

  return (user?.email || "").substring(0, 2).toUpperCase();
};

export const priceFormatter = (price) => {
  return new Intl.NumberFormat("sr-latn-RS", {
    style: "currency",
    currency: "RSD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
    .format(price)
    .replace("RSD", "")
    .trim();
};

export const formatDate = (dateString) => {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleString("en-GB", { month: "short" });
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");

  return `${day} ${month} ${year}, ${hours}:${minutes}h`;
};

export const convertOrdersToCSV = (orders) => {
  // CSV Headers
  const headers = [
    "Broj Porudžbine",
    "Ime",
    "Prezime",
    "Email",
    "Telefon",
    "Adresa",
    "Grad",
    "Poštanski Broj",
    "Država",
    "Kursevi",
    "Međuzbir",
    "Popust",
    "Ukupno",
    "Promo Kod",
    "Datum Kreiranja",
  ];

  // CSV Rows
  const rows = orders.map((order) => {
    const courses =
      order.items?.map((item) => item.courseTitle).join("; ") || "Nema stavki";

    return [
      order.orderNumber,
      order.firstName,
      order.lastName,
      order.email,
      order.phone || "",
      order.address || "",
      order.city || "",
      order.postalCode || "",
      order.country || "",
      courses,
      `${priceFormatter(order.subtotal)} RSD`,
      `${priceFormatter(order.discount)} RSD`,
      `${priceFormatter(order.total)} RSD`,
      order.appliedPromoCode || "",

      new Date(order.createdAt).toLocaleString("sr-latn-RS", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }),
    ];
  });

  // Escape CSV cells properly
  const escapeCsvCell = (cell) => {
    if (cell === null || cell === undefined) return "";
    const cellStr = String(cell);
    // If cell contains comma, quote, or newline, wrap in quotes and escape quotes
    if (
      cellStr.includes(",") ||
      cellStr.includes('"') ||
      cellStr.includes("\n")
    ) {
      return `"${cellStr.replace(/"/g, '""')}"`;
    }
    return cellStr;
  };

  // Build CSV content
  const csvContent = [
    headers.map(escapeCsvCell).join(","),
    ...rows.map((row) => row.map(escapeCsvCell).join(",")),
  ].join("\n");

  return csvContent;
};

export const downloadCSV = (csv, filename) => {
  // Add BOM for proper UTF-8 encoding in Excel
  const BOM = "\uFEFF";
  const blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up
  URL.revokeObjectURL(url);
};

export const convertOrdersToExcel = (orders) => {
  // Prepare data for Excel
  const excelData = orders.map((order) => {
    const courses =
      order.items?.map((item) => item.courseTitle).join("; ") || "Nema stavki";

    return {
      "Broj Porudžbine": order.orderNumber,
      Ime: order.firstName,
      Prezime: order.lastName,
      Email: order.email,
      Telefon: order.phone || "",
      Adresa: order.address || "",
      Grad: order.city || "",
      "Poštanski Broj": order.postalCode || "",
      Država: order.country || "",
      Kursevi: courses,
      Međuzbir: parseFloat(order.subtotal || 0),
      Popust: parseFloat(order.discount || 0),
      Ukupno: parseFloat(order.total || 0),
      "Promo Kod": order.appliedPromoCode || "",
      "Datum Kreiranja": new Date(order.createdAt).toLocaleString(
        "sr-latn-RS",
        {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        },
      ),
    };
  });

  return excelData;
};

export const downloadExcel = (data, filename) => {
  // Create workbook and worksheet
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(data);

  // Set column widths for better readability
  const columnWidths = [
    { wch: 15 }, // Broj Porudžbine
    { wch: 12 }, // Ime
    { wch: 12 }, // Prezime
    { wch: 25 }, // Email
    { wch: 15 }, // Telefon
    { wch: 30 }, // Adresa
    { wch: 15 }, // Grad
    { wch: 12 }, // Poštanski Broj
    { wch: 12 }, // Država
    { wch: 40 }, // Kursevi
    { wch: 12 }, // Međuzbir
    { wch: 10 }, // Popust
    { wch: 12 }, // Ukupno
    { wch: 15 }, // Promo Kod
    { wch: 20 }, // Datum Kreiranja
  ];
  worksheet["!cols"] = columnWidths;

  // Style the header row
  const headerKeys = Object.keys(data[0] || {});

  // Header styling
  const headerStyle = {
    font: {
      name: "Calibri", // Font koji dobro podržava srpsku latinicu
      sz: 12,
      bold: true,
      color: { rgb: "FFFFFF" },
    },
    fill: {
      type: "pattern",
      patternType: "solid",
      fgColor: { rgb: "2F5233" }, // Tamno zelena boja za header
    },
    alignment: {
      horizontal: "center",
      vertical: "center",
      wrapText: true,
    },
    border: {
      top: { style: "thin", color: { rgb: "000000" } },
      bottom: { style: "thin", color: { rgb: "000000" } },
      left: { style: "thin", color: { rgb: "000000" } },
      right: { style: "thin", color: { rgb: "000000" } },
    },
  };

  // Data row styling
  const dataStyle = {
    font: {
      name: "Calibri",
      sz: 11,
    },
    alignment: {
      vertical: "center",
      wrapText: true,
    },
    border: {
      top: { style: "thin", color: { rgb: "CCCCCC" } },
      bottom: { style: "thin", color: { rgb: "CCCCCC" } },
      left: { style: "thin", color: { rgb: "CCCCCC" } },
      right: { style: "thin", color: { rgb: "CCCCCC" } },
    },
  };

  // Number style for monetary values
  const numberStyle = {
    ...dataStyle,
    numFmt: '#,##0.00 "RSD"',
    alignment: {
      horizontal: "right",
      vertical: "center",
    },
  };

  // Apply header styling
  headerKeys.forEach((key, colIndex) => {
    const cellAddress = XLSX.utils.encode_cell({ c: colIndex, r: 0 });
    if (!worksheet[cellAddress]) worksheet[cellAddress] = {};
    worksheet[cellAddress].s = headerStyle;
  });

  // Apply data styling
  for (let rowIndex = 1; rowIndex <= data.length; rowIndex++) {
    headerKeys.forEach((key, colIndex) => {
      const cellAddress = XLSX.utils.encode_cell({ c: colIndex, r: rowIndex });
      if (!worksheet[cellAddress]) worksheet[cellAddress] = {};

      // Apply number formatting to monetary columns
      if (key === "Međuzbir" || key === "Popust" || key === "Ukupno") {
        worksheet[cellAddress].s = numberStyle;
      } else {
        worksheet[cellAddress].s = dataStyle;
      }

      // Add alternating row colors for better readability
      if (rowIndex % 2 === 0) {
        worksheet[cellAddress].s = {
          ...worksheet[cellAddress].s,
          fill: {
            type: "pattern",
            patternType: "solid",
            fgColor: { rgb: "F8F9FA" }, // Svetlo siva za parne redove
          },
        };
      }
    });
  }

  // Set row heights
  const rowHeights = [
    { hpx: 25 }, // Header row height
    ...Array(data.length).fill({ hpx: 20 }), // Data rows height
  ];
  worksheet["!rows"] = rowHeights;

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, "Porudžbine");

  // Save file
  XLSX.writeFile(workbook, filename);
};
